/**
 * Utility functions for Google Calendar link generation, dual-timezone formatting,
 * and WhatsApp/LinkedIn sharing for TestingVala QA Events.
 *
 * Obeys TestingVala Engineering Blueprint & zero-dependency guidelines.
 */

/**
 * Format a Date object into Google Calendar ISO format (YYYYMMDDTHHmmssZ)
 */
export const formatGoogleCalendarDate = (date) => {
  if (!date || isNaN(new Date(date).getTime())) return '';
  const d = new Date(date);
  return d.toISOString().replace(/-|:|\.\d+/g, '');
};

/**
 * FEATURE 1: Generate Google Calendar Event Link
 * Default duration: 60 minutes if not specified.
 */
export const getGoogleCalendarUrl = (event) => {
  if (!event || !event.event_time) return '#';
  try {
    const startDate = new Date(event.event_time);
    if (isNaN(startDate.getTime())) return '#';

    const durationMinutes = event.duration_minutes || 60;
    const endDate = new Date(startDate.getTime() + durationMinutes * 60 * 1000);

    const startISO = formatGoogleCalendarDate(startDate);
    const endISO = formatGoogleCalendarDate(endDate);

    const title = encodeURIComponent(event.title || 'TestingVala QA Event');
    const descText = `${event.short_description || event.description || ''}\n\nRegister & Join: ${event.external_event_url || window.location.href}\n\nPowered by TestingVala (https://testingvala.com)`;
    const details = encodeURIComponent(descText);
    const location = encodeURIComponent(event.location || 'Online Webinar');

    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startISO}/${endISO}&details=${details}&location=${location}`;
  } catch (error) {
    console.error('Error generating Google Calendar URL:', error);
    return '#';
  }
};

/**
 * Helper to extract short timezone acronym (e.g. IST, EST, PST, GMT, UTC)
 */
export const getShortTzName = (date, timeZone) => {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZoneName: 'short',
      timeZone,
    });
    const parts = formatter.formatToParts(date);
    const tzPart = parts.find(p => p.type === 'timeZoneName');
    return tzPart ? tzPart.value : timeZone;
  } catch {
    return timeZone || 'UTC';
  }
};

/**
 * FEATURE 2: Dual Timezone Formatter (IST + Browser Local Timezone)
 * Returns object with IST string and optional Local Time string.
 */
export const formatDualTimezone = (eventTime, customUserTimezone = null) => {
  if (!eventTime) {
    return { istText: 'TBD', localText: null, isSameTimezone: true };
  }

  try {
    const date = new Date(eventTime);
    if (isNaN(date.getTime())) {
      return { istText: 'TBD', localText: null, isSameTimezone: true };
    }

    // 1. Format in Indian Standard Time (IST - Asia/Kolkata)
    const istTimeStr = date.toLocaleTimeString('en-US', {
      timeZone: 'Asia/Kolkata',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    const istText = `${istTimeStr} IST`;

    // 2. Detect User Timezone
    const userTz = customUserTimezone || (typeof Intl !== 'undefined' && Intl.DateTimeFormat().resolvedOptions().timeZone) || 'UTC';

    // 3. Check if user is in IST (Asia/Kolkata or +05:30 offset)
    const isIndia = userTz === 'Asia/Kolkata' || userTz === 'Asia/Calcutta';

    if (isIndia) {
      return {
        istText,
        localText: null,
        isSameTimezone: true,
        fullDisplay: istText
      };
    }

    // 4. Format in User's Local Timezone
    const localTimeStr = date.toLocaleTimeString('en-US', {
      timeZone: userTz,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    const tzShort = getShortTzName(date, userTz);
    const localText = `Your Time: ${localTimeStr} ${tzShort}`;

    return {
      istText,
      localText,
      isSameTimezone: false,
      fullDisplay: `${istText} (${localText})`
    };
  } catch (error) {
    console.error('Error formatting dual timezone:', error);
    return { istText: 'TBD', localText: null, isSameTimezone: true };
  }
};

/**
 * FEATURE 3: WhatsApp Share Link Generator
 */
export const getWhatsAppShareUrl = (event, customUrl = null) => {
  if (!event) return '#';
  try {
    const url = customUrl || event.external_event_url || window.location.href;
    const eventDate = event.event_time ? new Date(event.event_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Upcoming';
    const tzInfo = formatDualTimezone(event.event_time);

    const shareText = `🚀 *QA Webinar: ${event.title || 'Software Testing Event'}*\n\n🗓 *Date:* ${eventDate}\n🕐 *Time:* ${tzInfo.istText}\n\n👉 *Register Free:* ${url}\n\nDiscovered on TestingVala (https://testingvala.com)`;

    // Detect if desktop non-touch browser to use web.whatsapp.com fallback if desired,
    // api.whatsapp.com handles auto-redirect to desktop Web or native mobile app.
    const isMobile = typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const baseUrl = isMobile ? 'https://api.whatsapp.com/send' : 'https://web.whatsapp.com/send';

    return `${baseUrl}?text=${encodeURIComponent(shareText)}`;
  } catch (error) {
    console.error('Error generating WhatsApp share URL:', error);
    return '#';
  }
};

/**
 * FEATURE 4: LinkedIn Share Link Generator
 */
export const getLinkedInShareUrl = (event, customUrl = null) => {
  if (!event) return '#';
  try {
    const url = customUrl || event.external_event_url || window.location.href;
    return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
  } catch (error) {
    console.error('Error generating LinkedIn share URL:', error);
    return '#';
  }
};
